import { API } from "@swivl-mono/api-helper";
import { BrainType, CoreType, NLPModelCache } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize } from "@swivl-mono/ui";
import { useEffect, useState } from "react";

import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";

interface NLPModelCacheWithDates extends NLPModelCache {
  createdAt?: string;
  updatedAt?: string;
}

interface CLUProject {
  projectName: string;
  projectKind: string;
  language: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  lastTrainedDateTime?: string;
  lastDeployedDateTime?: string;
}

interface CLUDeployment {
  deploymentName: string;
  modelId: string;
  lastTrainedDateTime: string;
  lastDeployedDateTime: string;
  deploymentExpirationDate?: string;
  modelTrainingConfigVersion?: string;
}

interface CLUTrainedModel {
  label: string;
  modelId: string;
  lastTrainedDateTime: string;
  lastTrainingDurationInSeconds: number;
  modelExpirationDate?: string;
  modelTrainingConfigVersion?: string;
}

interface DeployFormState {
  projectName: string;
  deploymentName: string;
  selectedModel: string;
}

interface TrainFormState {
  projectName: string;
  modelLabel: string;
  trainingMode: "standard" | "advanced";
}

export function NLPCachesView(): JSX.Element {
  const [caches, setCaches] = useState<NLPModelCacheWithDates[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set());
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  const [cluProjects, setCluProjects] = useState<CLUProject[]>([]);
  const [cluLoading, setCluLoading] = useState(true);
  const [deployments, setDeployments] = useState<Record<string, CLUDeployment[]>>({});
  const [deploymentsLoading, setDeploymentsLoading] = useState<Set<string>>(new Set());

  const [trainedModels, setTrainedModels] = useState<Record<string, CLUTrainedModel[]>>({});
  const [deployForm, setDeployForm] = useState<DeployFormState | null>(null);
  const [deploying, setDeploying] = useState(false);

  const [trainForm, setTrainForm] = useState<TrainFormState | null>(null);
  const [training, setTraining] = useState(false);
  const [exportingCLUIds, setExportingCLUIds] = useState<Set<string>>(new Set());

  const [cacheEdits, setCacheEdits] = useState<Record<string, { appId: string; version: string }>>({});
  const [cacheDeployments, setCacheDeployments] = useState<Record<string, CLUDeployment[]>>({});
  const [cacheDeploymentsLoading, setCacheDeploymentsLoading] = useState<Set<string>>(new Set());
  const [savingCacheIds, setSavingCacheIds] = useState<Set<string>>(new Set());

  const loadCaches = (): void => {
    setLoading(true);
    API.NLP.getCacheByCoreType(CoreType.STORAGE)
      .then((res) => {
        setCaches(res as NLPModelCacheWithDates[]);
      })
      .catch(() => {
        AlertsModel.errorToast("Error loading NLP caches");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadCLUProjects = (): void => {
    setCluLoading(true);
    API.NLP.getCLUProjects()
      .then((res) => {
        setCluProjects(res);
      })
      .catch(() => {
        AlertsModel.errorToast("Error loading CLU projects");
      })
      .finally(() => {
        setCluLoading(false);
      });
  };

  useEffect(() => {
    loadCaches();
    loadCLUProjects();
  }, []);

  useEffect(() => {
    caches.forEach((cache) => {
      if (cache.modelType === BrainType.CLU && cache.appId && !cacheDeployments[cache.appId]) {
        handleLoadCacheDeployments(cache.appId);
      }
    });
  }, [caches]);

  const handleLoadCacheDeployments = (projectName: string): void => {
    setCacheDeploymentsLoading((prev) => new Set(prev).add(projectName));
    API.NLP.getCLUDeployments(projectName)
      .then((res) => {
        setCacheDeployments((prev) => ({ ...prev, [projectName]: res }));
      })
      .catch(() => {
        AlertsModel.errorToast(`Error loading deployments for ${projectName}`);
      })
      .finally(() => {
        setCacheDeploymentsLoading((prev) => {
          const next = new Set(prev);
          next.delete(projectName);
          return next;
        });
      });
  };

  const handleCacheProjectChange = (cache: NLPModelCacheWithDates, newProjectName: string): void => {
    setCacheEdits((prev) => ({ ...prev, [cache.id]: { appId: newProjectName, version: "" } }));
    if (!cacheDeployments[newProjectName]) {
      handleLoadCacheDeployments(newProjectName);
    }
  };

  const handleCacheDeploymentChange = (cache: NLPModelCacheWithDates, newDeployment: string): void => {
    const current = cacheEdits[cache.id] || { appId: cache.appId, version: cache.version };
    setCacheEdits((prev) => ({ ...prev, [cache.id]: { ...current, version: newDeployment } }));
  };

  const handleSaveCacheConfig = (cache: NLPModelCacheWithDates): void => {
    const edit = cacheEdits[cache.id];
    if (!edit) return;
    setSavingCacheIds((prev) => new Set(prev).add(cache.id));
    API.NLP.updateCacheConfig(cache.id, edit.appId, edit.version)
      .then(() => {
        AlertsModel.successToast("Cache configuration updated");
        setCacheEdits((prev) => {
          const next = { ...prev };
          delete next[cache.id];
          return next;
        });
        loadCaches();
      })
      .catch(() => {
        AlertsModel.errorToast("Error updating cache configuration");
      })
      .finally(() => {
        setSavingCacheIds((prev) => {
          const next = new Set(prev);
          next.delete(cache.id);
          return next;
        });
      });
  };

  const handleRefresh = (cacheId: string): void => {
    setRefreshingIds((prev) => new Set(prev).add(cacheId));
    API.NLP.refreshCache(cacheId)
      .then(() => {
        AlertsModel.successToast("Cache refreshed");
        loadCaches();
      })
      .catch(() => {
        AlertsModel.errorToast("Error refreshing cache");
      })
      .finally(() => {
        setRefreshingIds((prev) => {
          const next = new Set(prev);
          next.delete(cacheId);
          return next;
        });
      });
  };

  const handleRefreshAll = async (): Promise<void> => {
    setRefreshingAll(true);
    for (const cache of caches) {
      try {
        await API.NLP.refreshCache(cache.id);
      } catch {
        AlertsModel.errorToast(`Error refreshing cache: ${cache.name}`);
      }
    }
    AlertsModel.successToast("All caches refreshed");
    loadCaches();
    setRefreshingAll(false);
  };

  const handleDownloadUtterances = (cache: NLPModelCacheWithDates): void => {
    setDownloadingIds((prev) => new Set(prev).add(cache.id));
    API.NLP.getLUISUtterances(cache.appId, cache.version)
      .then((utterances) => {
        const labeled = utterances.map((utterance) => {
          const entities = utterance.entityLabels.map((entity) => {
            // Convert token indices to character offset/length
            let charPos = 0;
            for (let i = 0; i < entity.startTokenIndex; i++) {
              const tokenIdx = utterance.text.indexOf(utterance.tokenizedText[i], charPos);
              charPos = tokenIdx + utterance.tokenizedText[i].length;
            }
            const startOffset = utterance.text.indexOf(utterance.tokenizedText[entity.startTokenIndex], charPos);

            let endCharPos = startOffset;
            for (let i = entity.startTokenIndex; i <= entity.endTokenIndex; i++) {
              const tokenIdx = utterance.text.indexOf(utterance.tokenizedText[i], endCharPos);
              endCharPos = tokenIdx + utterance.tokenizedText[i].length;
            }

            return {
              category: entity.entityName,
              offset: startOffset,
              length: endCharPos - startOffset
            };
          });

          return {
            intent: utterance.intentLabel,
            language: "en-us",
            text: utterance.text,
            entities
          };
        });
        const json = JSON.stringify(labeled, null, 2);
        const blob = new Blob([json], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${cache.name}-utterances.json`;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => {
        AlertsModel.errorToast("Error downloading utterances");
      })
      .finally(() => {
        setDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(cache.id);
          return next;
        });
      });
  };

  const handleLoadDeployments = (projectName: string): void => {
    setDeploymentsLoading((prev) => new Set(prev).add(projectName));
    API.NLP.getCLUDeployments(projectName)
      .then((res) => {
        setDeployments((prev) => ({ ...prev, [projectName]: res }));
      })
      .catch(() => {
        AlertsModel.errorToast(`Error loading deployments for ${projectName}`);
      })
      .finally(() => {
        setDeploymentsLoading((prev) => {
          const next = new Set(prev);
          next.delete(projectName);
          return next;
        });
      });
  };

  const handleOpenDeployForm = (projectName: string): void => {
    setDeployForm({ projectName, deploymentName: "", selectedModel: "" });
    if (!trainedModels[projectName]) {
      API.NLP.getCLUTrainedModels(projectName)
        .then((res) => {
          setTrainedModels((prev) => ({ ...prev, [projectName]: res }));
        })
        .catch(() => {
          AlertsModel.errorToast(`Error loading trained models for ${projectName}`);
        });
    }
  };

  const handleDeploy = async (): Promise<void> => {
    if (!deployForm || !deployForm.deploymentName || !deployForm.selectedModel) {
      AlertsModel.errorToast("Please fill in deployment name and select a model");
      return;
    }
    setDeploying(true);
    try {
      await API.NLP.createCLUDeployment(deployForm.projectName, deployForm.deploymentName, deployForm.selectedModel);
      AlertsModel.successToast(`Deployment "${deployForm.deploymentName}" created successfully`);
      setDeployForm(null);
      handleLoadDeployments(deployForm.projectName);
    } catch {
      AlertsModel.errorToast("Error creating deployment");
    } finally {
      setDeploying(false);
    }
  };

  const handleExportAsCLU = (cache: NLPModelCacheWithDates): void => {
    setExportingCLUIds((prev) => new Set(prev).add(cache.id));
    API.NLP.getLUISUtterances(cache.appId, cache.version)
      .then((utterances) => {
        // Convert LUIS utterances to CLU utterance format
        const cluUtterances = utterances.map((utterance) => {
          const entities = utterance.entityLabels.map((entity) => {
            let charPos = 0;
            for (let i = 0; i < entity.startTokenIndex; i++) {
              const tokenIdx = utterance.text.indexOf(utterance.tokenizedText[i], charPos);
              charPos = tokenIdx + utterance.tokenizedText[i].length;
            }
            const startOffset = utterance.text.indexOf(utterance.tokenizedText[entity.startTokenIndex], charPos);

            let endCharPos = startOffset;
            for (let i = entity.startTokenIndex; i <= entity.endTokenIndex; i++) {
              const tokenIdx = utterance.text.indexOf(utterance.tokenizedText[i], endCharPos);
              endCharPos = tokenIdx + utterance.tokenizedText[i].length;
            }

            return {
              category: entity.entityName,
              offset: startOffset,
              length: endCharPos - startOffset
            };
          });

          return {
            intent: utterance.intentLabel,
            language: "en-us",
            text: utterance.text,
            dataset: "Train",
            entities
          };
        });

        // Extract unique intents and entities
        const intentSet = new Set<string>();
        const entitySet = new Set<string>();
        for (const u of cluUtterances) {
          intentSet.add(u.intent);
          for (const e of u.entities) {
            entitySet.add(e.category);
          }
        }

        const cluProject = {
          projectFileVersion: "2022-10-01-preview",
          stringIndexType: "Utf16CodeUnit",
          metadata: {
            projectKind: "Conversation",
            projectName: cache.name,
            multilingual: true,
            language: "en-us",
            settings: {
              confidenceThreshold: 0
            }
          },
          assets: {
            projectKind: "Conversation",
            intents: Array.from(intentSet).map((name) => ({ category: name })),
            entities: Array.from(entitySet).map((name) => ({ category: name })),
            utterances: cluUtterances
          }
        };

        const json = JSON.stringify(cluProject, null, 2);
        const blob = new Blob([json], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${cache.name}-clu.json`;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => {
        AlertsModel.errorToast("Error exporting as CLU project");
      })
      .finally(() => {
        setExportingCLUIds((prev) => {
          const next = new Set(prev);
          next.delete(cache.id);
          return next;
        });
      });
  };

  const handleTrain = async (): Promise<void> => {
    if (!trainForm || !trainForm.modelLabel) {
      AlertsModel.errorToast("Please enter a model label");
      return;
    }
    setTraining(true);
    try {
      await API.NLP.trainCLUModel(trainForm.projectName, trainForm.modelLabel, trainForm.trainingMode);
      AlertsModel.successToast(`Training started for model "${trainForm.modelLabel}"`);
      setTrainForm(null);
      loadCLUProjects();
    } catch {
      AlertsModel.errorToast("Error starting training");
    } finally {
      setTraining(false);
    }
  };

  return (
    <div className="p-5">
      {/* Caches Section */}
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-3xl text-grey-800">NLP Model Caches</h1>
        <Button
          color={ButtonColor.ORANGE}
          size={ButtonSize.SMALL}
          loading={refreshingAll}
          onClick={handleRefreshAll}
          disabled={loading || caches.length === 0}
        >
          Refresh All
        </Button>
      </div>

      {loading && <p className="text-grey-500">Loading caches...</p>}

      {!loading && caches.length === 0 && <p className="text-grey-500">No caches found.</p>}

      {!loading && caches.length > 0 && (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-grey-200 text-grey-500">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">App ID / Project</th>
              <th className="px-3 py-2">Version / Deployment</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Intents</th>
              <th className="px-3 py-2">Entities</th>
              <th className="px-3 py-2">Updated</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {caches.map((cache) => {
              const isCLU = cache.modelType === BrainType.CLU;
              const edit = cacheEdits[cache.id];
              const currentAppId = edit?.appId ?? cache.appId;
              const currentVersion = edit?.version ?? cache.version;
              const hasChanges = edit && (edit.appId !== cache.appId || edit.version !== cache.version);

              return (
                <tr key={cache.id} className="border-b border-grey-100">
                  <td className="px-3 py-2 font-medium text-grey-800">{cache.name}</td>
                  <td className="px-3 py-2">
                    {isCLU ? (
                      <select
                        className="rounded border border-grey-200 px-2 py-1 text-sm"
                        value={currentAppId}
                        onChange={(e) => handleCacheProjectChange(cache, e.target.value)}
                      >
                        <option value="">Select project...</option>
                        {cluProjects.map((p) => (
                          <option key={p.projectName} value={p.projectName}>
                            {p.projectName}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="font-mono text-xs text-grey-600">{cache.appId}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isCLU ? (
                      <select
                        className="rounded border border-grey-200 px-2 py-1 text-sm"
                        value={currentVersion}
                        onChange={(e) => handleCacheDeploymentChange(cache, e.target.value)}
                        disabled={!currentAppId || cacheDeploymentsLoading.has(currentAppId)}
                      >
                        <option value="">{cacheDeploymentsLoading.has(currentAppId) ? "Loading..." : "Select deployment..."}</option>
                        {(cacheDeployments[currentAppId] || []).map((dep) => (
                          <option key={dep.deploymentName} value={dep.deploymentName}>
                            {dep.deploymentName}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-grey-600">{cache.version}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-grey-600">{cache.modelType}</td>
                  <td className="px-3 py-2 text-grey-600">{cache.intents?.length ?? 0}</td>
                  <td className="px-3 py-2 text-grey-600">{cache.entities?.length ?? 0}</td>
                  <td className="px-3 py-2 text-xs text-grey-500">{cache.updatedAt ? new Date(cache.updatedAt).toLocaleString() : "-"}</td>
                  <td className="flex gap-2 px-3 py-2">
                    {isCLU && hasChanges && (
                      <Button
                        color={ButtonColor.ORANGE}
                        size={ButtonSize.SMALL}
                        loading={savingCacheIds.has(cache.id)}
                        onClick={() => handleSaveCacheConfig(cache)}
                      >
                        Save
                      </Button>
                    )}
                    <Button
                      color={ButtonColor.TEAL}
                      size={ButtonSize.SMALL}
                      loading={refreshingIds.has(cache.id)}
                      onClick={() => handleRefresh(cache.id)}
                    >
                      Refresh
                    </Button>
                    {!isCLU && (
                      <>
                        <Button
                          color={ButtonColor.LIGHT_GREY}
                          size={ButtonSize.SMALL}
                          loading={downloadingIds.has(cache.id)}
                          onClick={() => handleDownloadUtterances(cache)}
                        >
                          Download Utterances
                        </Button>
                        <Button
                          color={ButtonColor.LIGHT_GREY}
                          size={ButtonSize.SMALL}
                          loading={exportingCLUIds.has(cache.id)}
                          onClick={() => handleExportAsCLU(cache)}
                        >
                          Export as CLU
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* CLU Models Section */}
      <div className="mt-10 mb-4 flex items-center justify-between">
        <h1 className="text-3xl text-grey-800">CLU Models</h1>
      </div>

      {cluLoading && <p className="text-grey-500">Loading CLU projects...</p>}

      {!cluLoading && cluProjects.length === 0 && <p className="text-grey-500">No CLU projects found.</p>}

      {!cluLoading && cluProjects.length > 0 && (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-grey-200 text-grey-500">
              <th className="px-3 py-2">Project Name</th>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">Language</th>
              <th className="px-3 py-2">Last Modified</th>
              <th className="px-3 py-2">Last Trained</th>
              <th className="px-3 py-2">Last Deployed</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {cluProjects.map((project) => (
              <>
                <tr key={project.projectName} className="border-b border-grey-100">
                  <td className="px-3 py-2 font-medium text-grey-800">{project.projectName}</td>
                  <td className="px-3 py-2 text-grey-600">{project.projectKind}</td>
                  <td className="px-3 py-2 text-grey-600">{project.language}</td>
                  <td className="px-3 py-2 text-xs text-grey-500">
                    {project.lastModifiedDateTime ? new Date(project.lastModifiedDateTime).toLocaleString() : "-"}
                  </td>
                  <td className="px-3 py-2 text-xs text-grey-500">
                    {project.lastTrainedDateTime ? new Date(project.lastTrainedDateTime).toLocaleString() : "-"}
                  </td>
                  <td className="px-3 py-2 text-xs text-grey-500">
                    {project.lastDeployedDateTime ? new Date(project.lastDeployedDateTime).toLocaleString() : "-"}
                  </td>
                  <td className="flex gap-2 px-3 py-2">
                    <Button
                      color={ButtonColor.TEAL}
                      size={ButtonSize.SMALL}
                      loading={deploymentsLoading.has(project.projectName)}
                      onClick={() => handleLoadDeployments(project.projectName)}
                    >
                      {deployments[project.projectName] ? "Reload" : "Deployments"}
                    </Button>
                    <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} onClick={() => handleOpenDeployForm(project.projectName)}>
                      Deploy
                    </Button>
                    <Button
                      color={ButtonColor.BLUE}
                      size={ButtonSize.SMALL}
                      onClick={() => setTrainForm({ projectName: project.projectName, modelLabel: "", trainingMode: "standard" })}
                    >
                      Train
                    </Button>
                  </td>
                </tr>
                {/* Train form */}
                {trainForm && trainForm.projectName === project.projectName && (
                  <tr key={project.projectName + "-train-form"}>
                    <td colSpan={7} className="bg-teal-50 px-6 py-4">
                      <div className="flex items-end gap-4">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-grey-600">Model Label</label>
                          <input
                            className="rounded border border-grey-200 px-2 py-1.5 text-sm"
                            type="text"
                            placeholder="e.g. model-v1"
                            value={trainForm.modelLabel}
                            onChange={(e) => setTrainForm({ ...trainForm, modelLabel: e.target.value })}
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-grey-600">Training Mode</label>
                          <select
                            className="rounded border border-grey-200 px-2 py-1.5 text-sm"
                            value={trainForm.trainingMode}
                            onChange={(e) => setTrainForm({ ...trainForm, trainingMode: e.target.value as "standard" | "advanced" })}
                          >
                            <option value="standard">Standard</option>
                            <option value="advanced">Advanced</option>
                          </select>
                        </div>
                        <Button color={ButtonColor.BLUE} size={ButtonSize.SMALL} loading={training} onClick={handleTrain}>
                          Start Training
                        </Button>
                        <Button color={ButtonColor.LIGHT_GREY} size={ButtonSize.SMALL} onClick={() => setTrainForm(null)}>
                          Cancel
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
                {/* Deploy form */}
                {deployForm && deployForm.projectName === project.projectName && (
                  <tr key={project.projectName + "-deploy-form"}>
                    <td colSpan={7} className="bg-orange-50 px-6 py-4">
                      <div className="flex items-end gap-4">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-grey-600">Trained Model</label>
                          <select
                            className="rounded border border-grey-200 px-2 py-1.5 text-sm"
                            value={deployForm.selectedModel}
                            onChange={(e) => setDeployForm({ ...deployForm, selectedModel: e.target.value })}
                          >
                            <option value="">Select a model...</option>
                            {(trainedModels[project.projectName] || []).map((model) => (
                              <option key={model.label} value={model.label}>
                                {model.label} (trained {new Date(model.lastTrainedDateTime).toLocaleDateString()})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-grey-600">Deployment Name</label>
                          <input
                            className="rounded border border-grey-200 px-2 py-1.5 text-sm"
                            type="text"
                            placeholder="e.g. production"
                            value={deployForm.deploymentName}
                            onChange={(e) => setDeployForm({ ...deployForm, deploymentName: e.target.value })}
                          />
                        </div>
                        <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} loading={deploying} onClick={handleDeploy}>
                          Create Deployment
                        </Button>
                        <Button color={ButtonColor.LIGHT_GREY} size={ButtonSize.SMALL} onClick={() => setDeployForm(null)}>
                          Cancel
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
                {/* Deployments table */}
                {deployments[project.projectName] && (
                  <tr key={project.projectName + "-deployments"}>
                    <td colSpan={7} className="bg-grey-50 px-6 py-3">
                      {deployments[project.projectName].length === 0 ? (
                        <p className="text-sm text-grey-500">No deployments found.</p>
                      ) : (
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="text-grey-400">
                              <th className="px-2 py-1">Deployment Name</th>
                              <th className="px-2 py-1">Model ID</th>
                              <th className="px-2 py-1">Training Config</th>
                              <th className="px-2 py-1">Last Trained</th>
                              <th className="px-2 py-1">Last Deployed</th>
                              <th className="px-2 py-1">Expires</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deployments[project.projectName].map((dep) => (
                              <tr key={dep.deploymentName} className="border-b border-grey-100">
                                <td className="px-2 py-1 font-medium text-grey-800">{dep.deploymentName}</td>
                                <td className="px-2 py-1 font-mono text-grey-600">{dep.modelId}</td>
                                <td className="px-2 py-1 text-grey-600">{dep.modelTrainingConfigVersion || "-"}</td>
                                <td className="px-2 py-1 text-grey-500">
                                  {dep.lastTrainedDateTime ? new Date(dep.lastTrainedDateTime).toLocaleString() : "-"}
                                </td>
                                <td className="px-2 py-1 text-grey-500">
                                  {dep.lastDeployedDateTime ? new Date(dep.lastDeployedDateTime).toLocaleString() : "-"}
                                </td>
                                <td className="px-2 py-1 text-grey-500">{dep.deploymentExpirationDate || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
